import { FullstoryInterceptor } from './../shared/interceptors/fullstory.interceptor';
import { LenderCodeInterceptor } from './../shared/interceptors/lender-code-interceptor';
import { ErrorHandlerService } from '@shared/service/error-handler.service';
import { RetryInterceptor } from './../shared/interceptors/retry.interceptor';
import { CurrencyPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  FooterModule,
  GlobalHeaderLibraryModule,
  GoogleTagManagerService,
  SharedUiLibraryModule,
  ThemeService,
  FeatureFlagService,
  AppConfigService as SharedAppConfigService,
  FeatureFlag
} from '@cudirect/shared-ui-library';
import { PathGuardService } from '@shared/service/path-guard-service.service';
import { environment } from 'environments/environment';
import { AppConfigService } from 'src/app/config/config.service';
import { HeaderInterceptor } from '@shared/interceptors/header.interceptor';
import { UserSessionService } from 'src/shared/service/user-session.service';
import { SharedModule } from 'src/shared/shared.module';
import { WorkflowModule } from 'src/workflow/workflow.module';
import { AuthenticateGuard } from '../shared/service/authenticateguard.service';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routes';
import { PageNotFoundComponent } from './page/error/404/404.component';
import { GenericErrorComponent } from './page/generic_error/generic_error.component';
import { RedirectComponent } from './page/redirect/redirect.component';
import { InitializeLenderService } from './service/initialize-lender.service';
import { InitializeWorkflowWidgetService } from './service/initialize-workflow-widget.service';
import { AdminLoginComponent } from './page/admin-login/admin-login.component';
import { EaAuthenticateModule } from '@cudirect-widgets/ea-login';
import { AdminLogoutComponent } from './page/admin-logout/admin-logout.component';
import { InitializeAppConfigDataService } from './service/initialize-app.config.service';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { AdminConfig } from '@shared/interfaces/appInitConfig';
import { OrigenceLegalComponent } from '../origence-legal/origence-legal.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { PickListTypes } from '@workflow/ecp-workflow/dynamic-app/types/picklist';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { PartnerConfigService } from '@workflow/ecp-shared/service/partner-config/partner-config.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ECP_WORKFLOW_ENVIRONMENT_TOKEN } from '@workflow/ecp-shared/service/environment/environment.service';
import { ProductsConfig } from '@workflow/class/config.type';
import { DynamicWorkflowModule } from '@workflow/dynamic-workflow.module';
import { ConfigService } from '@workflow/service/config/config.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { LenderService } from '@shared/service/lender/lender.service';
import { AlloyConfig } from '@workflow/ecp-workflow/dynamic-app/service/alloy/alloy-config.const';
import { UserVerificationErrorComponent } from './page/user-verification-error/user-verification-error.component';

// TODO: The 'SharedAppConfigService' is the correct one from SharedUI. It should replace both the local app config service
// and the environment data.

/** App Initializer Function
 ** TODO: Note that a promise failure in the APP_INIT chain can throw an 'ngOriginalError'.
 The reccomended approach to fixing this is a try/catch block, but that does not result in
 better UI until we tackle this in #106643. Something to be aware of.
 * @param initializeLenderService a reference to the Lender Service, which provides lender configurations required by the app
 * @param initializeWWService a reference to the Workflow Widget Service, which initializes workflow routes and configurations
 * @returns promise that app is expecting to resolve to allow APP_INIT to continue
 */
const appInit = (
  initializeLenderService: InitializeLenderService,
  initializeWWService: InitializeWorkflowWidgetService,
  featureFlagService: FeatureFlagService,
  configService: ConfigService,
  appConfigService: SharedAppConfigService,
  initializeAppConfigService: InitializeAppConfigDataService,
  adminPortalFeaturesService: AdminPortalFeaturesService,
  l360Service: L360Service
) => {
  // Need for cache busting
  const datetime = new Date().toISOString();

  return async () => {
    return await appConfigService
      .load('./assets/data/config.json?cb=' + datetime)
      .then(async () => {
        // Get and set all Consolidated Configs for (Lender, Workflow, Feature, Picklist, Admin)
        await initializeAppConfigService.initAppConfig();

        // Filter feature config
        const featureFlagConfig = initializeAppConfigService.getConfigByKey<FeatureFlag[]>('features');
        // set feature flags config data
        if (featureFlagConfig) featureFlagService.featureFlagsConfigData = featureFlagConfig;
        else initializeAppConfigService.handleInitFailure('Issue fetching Feature Flags.');

        // Filter lender config
        const lenderConfig = initializeAppConfigService.getConfigByKey<LenderConfiguration>('lender');
        // set lender config data
        if (lenderConfig) initializeLenderService.setLenderConfigData(lenderConfig);
        else initializeAppConfigService.handleInitFailure('Issue fetching Lender Config.');

        // Filter workflow config
        const workflowConfig = initializeAppConfigService.getConfigByKey<ProductsConfig>('workflow');
        // set workflow config data
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (workflowConfig) initializeWWService.setConfigData(environment['externalWorkflowConfig'].configApi, workflowConfig);
        else initializeAppConfigService.handleInitFailure('Issue fetching Workflow Config.');

        // Filter admin config
        const adminConfig = initializeAppConfigService.getConfigByKey<AdminConfig>('admin');
        if (adminConfig) adminPortalFeaturesService.config = adminConfig;
        else initializeAppConfigService.handleInitFailure('Issue fetching Admin Config.');

        // TODO: What is this doing?
        configService.addExternalConfig({ oao: { admin: adminConfig } });

        // Filter picklist config
        const pickListConfig = initializeAppConfigService.getConfigByKey<PickListTypes>('picklist');
        if (pickListConfig) l360Service.pickListConfigData = pickListConfig;
        else initializeAppConfigService.handleInitFailure('Issue fetching Picklist Config.');

        // sessionStorage.setItem('appOriginatedURL', location.href); // Getting app loading url for reporting
        // Unfortunately, this broke the appOriginatedURL, so we're using the StatefulValue service for 4.1
        // We should revisit this in trunk, and come up with a better solution
        // Currently, workflow is looking in sessionStore under this key cuASSD:ss
        // for anything saved in workflow configs using #{ss:somekey}
        new StatefulValue('ss:appOriginatedURL').value = location.href; // Getting app loading url for reporting
      })
      .catch((error) => initializeAppConfigService.handleInitFailure(error));
  };
};

// Root Module
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    PageNotFoundComponent,
    GenericErrorComponent,
    UserVerificationErrorComponent,
    AdminLoginComponent,
    RedirectComponent,
    AdminLogoutComponent,
    OrigenceLegalComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    RouterModule.forRoot([], { anchorScrolling: 'disabled', onSameUrlNavigation: 'reload', scrollPositionRestoration: 'top' }),
    BrowserAnimationsModule,
    SharedModule.forRoot(),
    SharedUiLibraryModule,
    FooterModule,
    GlobalHeaderLibraryModule,
    DynamicWorkflowModule,
    WorkflowModule,
    EaAuthenticateModule,
    NgxMaskDirective,
    NgxMaskPipe
  ],
  providers: [
    ApiService,
    SharedAppConfigService,
    AuthenticateGuard,
    PathGuardService,
    GlobalService,
    PartnerConfigService,
    UserService,
    AppConfigService,
    GoogleTagManagerService,
    ThemeService,
    LenderService,
    provideNgxMask(),
    {
      provide: ECP_WORKFLOW_ENVIRONMENT_TOKEN,
      useFactory: () => environment
    },
    {
      deps: [
        InitializeLenderService,
        InitializeWorkflowWidgetService,
        FeatureFlagService,
        ConfigService,
        SharedAppConfigService,
        InitializeAppConfigDataService,
        AdminPortalFeaturesService,
        L360Service
      ],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: appInit
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LenderCodeInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FullstoryInterceptor,
      multi: true
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (appConfig: AppConfigService) => {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        return appConfig.getData()?.recaptchaSiteKey || environment['recaptchaSiteKey'];
      },
      deps: [AppConfigService]
    },
    UserSessionService,
    WorkflowService,
    CurrencyPipe,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: AlloyConfig,
      useFactory: (appConfig: AppConfigService) => {
        return {
          initParams: {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            production: appConfig.getData()?.isProd || environment['isProd'],
            showHeader: true,
            customStyle: {
              theme: {
                // AAA level status colors
                successColor: '#005E48',
                errorColor: '#B30813'
              }
            }
          },
          stepUpAnchor: 'id-verify-anchor',
          anchorIsDedicated: true
        };
      },
      deps: [AppConfigService]
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
